/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * SignInForm Component
 * --------
 * A reusable sign-in form component used to authenticate users.
 *
 * Props:
 *  - onSubmit: Function called on form submission with form data and error setter callback.
 *  - showSignUp: Boolean flag to conditionally render the sign-up redirect link.
 *
 * Features:
 *  - Manages internal form state for username/email and password fields.
 *  - Validates required fields locally before invoking external onSubmit handler.
 *  - Displays validation and submission error messages that auto-clear after a delay.
 *  - Integrates with react-router navigation to allow redirection (e.g., back button, sign-up link).
 *  - Pinned to the Agni palette (not ThemeContext), handed to SignInForm.css as CSS
 *    custom properties. See the comment on the `theme` constant for the reasoning.
 *  - Renders the themed 'Route Trace' layout: an illustrative club route map and
 *    elevation profile alongside the credentials card.
 *  - Supports accessibility best practices including proper labels, aria attributes, and keyboard handling.
 *  - Implements a fading mechanism for error messages with timeout cleanup for a smooth user experience.
 *
 * Usage:
 *  <SignInForm onSubmit={handleSignIn} showSignUp={true} />
 *
 * Notes:
 *  - The onSubmit handler receives two arguments: form data object and setErrors callback.
 *  - The loading state disables the submit button and updates its label to indicate progress.
 *  - Contains a "Forgot password?" link directing users to password recovery.
 *  - Logo and app name always resolve from useAppBranding(), never hard-coded.
 *  - Renders a back button that navigates to the home page.
 */


import React, { useState, useEffect, useRef } from 'react';
import { agniTheme } from '../theme/themes';
import envConfig from '../config/envConfig';
import { useAppBranding } from '../hooks/useAppBranding';
// import VelLogo from './VelLogo';
import BrandMark from './brand/BrandMark';
import RouteMapArt, { ElevationProfile } from './signin/RouteMapArt';
import GoogleIcon from './GoogleIcon';
import APIHelper from '../api/APIHelper';
import { getProviderMeta, loadSsoProviders } from '../config/ssoProviders';
import { useNavigate } from 'react-router-dom';

import { withNext, stashSsoNext } from "../utils/returnTo";
import '../styles/SignInForm.css';
import { EMAIL_MAX_LEN, PASSWORD_MAX_LEN } from '../utils/authFieldRules';
const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * buildSsoAuthorizeUrl
 * --------
 * Pure function (no component state, no window.location reads beyond
 * `window.location.origin` for the redirect_uri) that builds the full-page
 * redirect URL to the backend's SsoAuthorizeAPIView (D-01).
 *
 * Extracted as a standalone export specifically so tests can assert on the
 * `intent`/`redirect_uri` query params without spying on or reassigning
 * `window.location` (flaky in JSDOM/CRA's Jest environment).
 */
// Previous version — one redirect_uri shape for every provider. Correct for
// Google, but Apple never arrives this way; see FORM_POST_PROVIDERS below.
// export function buildSsoAuthorizeUrl({ backendApiUrl, providerKey, intent, applicationId }) {
//   const redirectUri = `${window.location.origin}/auth/sso/callback/${providerKey}`;
//   const params = new URLSearchParams({
//     intent,
//     application_id: applicationId,
//     redirect_uri: redirectUri,
//   });
//   return `${backendApiUrl}/arcee/sso/${providerKey}/authorize/?${params.toString()}`;
// }

/**
 * Providers that answer with response_mode=form_post instead of a GET redirect.
 *
 * Apple requires form_post whenever name/email is requested, which means it
 * HTTP POSTs `state`/`code` to the Return URL. This app is a static SPA: a POST
 * to one of its routes never reaches React, and the values sit in a body no
 * client-side code can read. So Apple's registered Return URL is the backend's
 * bounce endpoint, and `return_to` names the page to land on afterwards —
 * SsoFormPostBounceAPIView 302s there with ?state&code, which SsoCallbackPage
 * already consumes provider-agnostically.
 */
const FORM_POST_PROVIDERS = ['apple'];

export function buildSsoAuthorizeUrl({ backendApiUrl, providerKey, intent, applicationId }) {
  const callbackUri = `${window.location.origin}/auth/sso/callback/${providerKey}`;
  const isFormPost = FORM_POST_PROVIDERS.includes(providerKey);
  const params = new URLSearchParams({
    intent,
    application_id: applicationId,
    redirect_uri: isFormPost
      ? `${backendApiUrl}/arcee/sso/${providerKey}/form-post/`
      : callbackUri,
  });
  // Both values are allowlisted server-side (SsoProviderConfig.redirect_uri_allowlist);
  // an unlisted one is a 400 from the authorize view, never a silent fallback.
  if (isFormPost) params.set('return_to', callbackUri);
  return `${backendApiUrl}/arcee/sso/${providerKey}/authorize/?${params.toString()}`;
}

function SignInForm({ onSubmit, showSignUp }) {
  // The sign-in screen is pinned to Agni rather than following the active theme.
  // Elsewhere the palette comes from the application's branding (and from the
  // visitor's stored choice in localStorage['app_theme']); this one screen is
  // deliberately fixed so it always presents the same way, whichever tenant's
  // branding is loaded. Change this single constant to repoint the screen.
  const theme = agniTheme;
  // const { data: branding } = useAppBranding();
  const { data: branding, isPending: brandingPending } = useAppBranding();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const errorTimeouts = useRef({});
  const [showPassword, setShowPassword] = useState(false);
  const [ssoProviders, setSsoProviders] = useState([]);

  useEffect(() => {
    let mounted = true;
    // Routed through the shared cache in config/ssoProviders, so the list is
    // fetched once per tab instead of once per mount of this form (twice per
    // mount in development, where StrictMode double-invokes effects).
    // The inline fetch this replaced is kept below for reference.
    //
    // (async () => {
    //   try {
    //     const resp = await apiHelper.apiCall('/arcee/sso/providers/', { method: 'GET' });
    //     if (!resp.ok) return;
    //     const data = await resp.json();
    //     if (mounted) setSsoProviders(Array.isArray(data) ? data : []);
    //   } catch (_err) {
    //     // Network/auth error — fail open, simply don't show SSO buttons.
    //   }
    // })();
    loadSsoProviders(apiHelper).then((list) => {
      if (mounted) setSsoProviders(list);
    });
    return () => { mounted = false; };
  }, []);

  const handleSsoClick = (providerKey) => {
    // Hold on to ?next= before leaving the SPA. The authorize round trip comes
    // back to an allowlisted callback URL that cannot carry the parameter, so
    // without this an anonymous visitor who clicked an event's action button,
    // was sent to /sign-in?next=<event detail>, and then chose Google was
    // signed in correctly and dropped on /feed — the registration they were
    // halfway through simply vanished. See stashSsoNext in utils/returnTo.
    stashSsoNext();

    // 'signup' (not 'signin') is intentional: arcee's resolve_sso_account treats
    // intent=signup as "log in if this account exists, else create one," which is
    // the correct behavior for a "Continue with Google" button. intent=signin would
    // 404 (no_account_for_sso_provider) on a first-time Google sign-in from this
    // page. Mirrors upvibe's SignInViewModel.buildGoogleAuthorizeUrl() for the same reason.
    window.location = buildSsoAuthorizeUrl({
      backendApiUrl: envConfig.backendApiUrl,
      providerKey,
      intent: 'signup',
      applicationId: envConfig.applicationId,
    });
  };

  const clearErrorAfterDelay = (field, delay = 4000) => {
    if (errorTimeouts.current[field]) clearTimeout(errorTimeouts.current[field]);
    errorTimeouts.current[field] = setTimeout(() => {
      setErrors(prev => ({ ...prev, [field]: '' }));
      delete errorTimeouts.current[field];
    }, delay);
  };

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Trim leading/trailing whitespace so accidental spaces (e.g. from autofill
    // or a trailing keyboard space) aren't submitted. Internal spaces are kept.
    const trimmed = { ...form, username: form.username.trim(), password: form.password.trim() };
    let errs = {};
    if (!trimmed.username) errs.username = "Username or email is required.";
    else if (trimmed.username.length > EMAIL_MAX_LEN)
      errs.username = `Username or email must be at most ${EMAIL_MAX_LEN} characters.`;
    if (!trimmed.password) errs.password = "Password is required.";
    else if (trimmed.password.length > PASSWORD_MAX_LEN)
      errs.password = `Password must be at most ${PASSWORD_MAX_LEN} characters.`;
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      // Start fade out timers for errors
      for (const field of Object.keys(errs)) {
        clearErrorAfterDelay(field);
      }
      return;
    }

    setLoading(true);
    try {
      await onSubmit(trimmed, setErrors); // Pass setErrors to handle server-side
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // For all current errors, start fadeout timer
    Object.keys(errors).forEach(field => {
      if (errors[field]) {
        clearErrorAfterDelay(field);
      }
    });
  }, [errors]);


  // Theme tokens are handed to the stylesheet as custom properties rather than
  // sprinkled through inline styles, so one set of rules covers all themes.
  // Guarded with fallbacks: consumers (and tests) may supply a partial theme.
  const hexAlpha = (hex, aa) =>
    (/^#[0-9a-fA-F]{6}$/.test(hex || '') ? `${hex}${aa}` : hex);
  const primary = theme?.primary || '#ff7a00';
  const secondary = theme?.secondary || theme?.primaryLight || primary;
  const themeVars = {
    '--sif-p': primary,
    '--sif-s': secondary,
    '--sif-p08': hexAlpha(primary, '14'),
    '--sif-p14': hexAlpha(primary, '24'),
    '--sif-p30': hexAlpha(primary, '4d'),
    '--sif-tint': hexAlpha(primary, '12'),
    '--sif-radius': theme?.buttonRadius || '0.5rem',
  };

  const appName = branding?.app_name || envConfig.appName;

  return (
    <div className="sif-shell" style={themeVars}>
      <div className="sif-halo" aria-hidden="true" />

      <div className="sif-grid">
        {/* ── Left: the route showcase ────────────────────────────
            Illustrative club route, not the visitor's own data — the overlay
            label says so, so nothing here reads as a personal activity. */}
        <div className="sif-left">
          <span className="sif-club"><i>IR</i> Indiranagar Runners Club &middot; Sunday long run</span>

          <h2 className="sif-title">Today&apos;s route is <em>already drawn.</em></h2>
          <p className="sif-sub">
            Sign in to log yours, claim your splits and share it with your club.
          </p>

          <div className="sif-act">
            <div className="sif-mapwrap">
              <RouteMapArt />
              <span className="sif-ov sif-ov-tl">Club route of the day &middot; Cubbon Park loop</span>
              <span className="sif-ov sif-ov-tr">GPS 1s</span>
              <span className="sif-scale">500 m<i /></span>
            </div>

            <div className="sif-elev">
              <div className="sif-elev-hd">
                <span>Elevation &middot; 214 m gain</span>
                <span>842 &ndash; 1,056 m</span>
              </div>
              <ElevationProfile />
              <div className="sif-elev-axis">
                <span>0 km</span><span>4</span><span>8</span><span>12.4 km</span>
              </div>
            </div>
          </div>

          <div className="sif-stats">
            <div className="sif-stat"><b>12.4<i>km</i></b><span>Distance</span></div>
            <div className="sif-stat"><b>4:41<i>/km</i></b><span>Avg pace</span></div>
            <div className="sif-stat"><b>214<i>m</i></b><span>Elev gain</span></div>
            <div className="sif-stat"><b>58:12</b><span>Moving time</span></div>
          </div>
        </div>

        {/* ── Right: the credentials card ─────────────────────── */}
        <div className="sif-card">
          <div className="sif-card-head">
            <button
              type="button"
              className="sif-back"
              onClick={() => navigate('/')}
              aria-label="Back to home"
            >
              <i className="bi bi-arrow-left" />
            </button>

            {/* The homepage's lockup (logo + Athlo/Hive wordmark), as in the
                public header. The name comes from branding; while that loads a
                placeholder shows instead of envConfig's fallback name. */}
            <div className="sif-brand">
              <BrandMark name={appName} logo={branding?.logo} pending={brandingPending} />
            </div>
            {
              // Previous logo + name, replaced by BrandMark above:
              // {/* Logo and name always come from the application's branding, with
              //     VelLogo / envConfig.appName as the fallbacks. */}
              // {branding?.logo ? (
              //   <img src={branding.logo} alt={appName} className="sif-brand-logo" />
              // ) : (
              //   <VelLogo size={38} themeOverride={theme} />
              // )}
              // <p className="sif-word">{appName}</p>
            }
          </div>

          <h1 className="sif-h1">Sign in</h1>

          <form onSubmit={handleSubmit} className="sif-form" noValidate>
            <div>
              <label htmlFor="username" className="sif-label">Username or Email</label>
              <input
                type="text"
                id="username"
                name="username"
                className="sif-input"
                placeholder="you@example.com or username"
                value={form.username}
                onChange={handleChange}
                maxLength={EMAIL_MAX_LEN}
                autoComplete="username"
              />
              {errors.username && (
                <div className="sif-error">{errors.username}</div>
              )}
            </div>

            <div>
              <label htmlFor="password" className="sif-label">Password</label>
              {/* The toggle is positioned against the input itself, not the whole
                  field, so it stays put when an error message appears below. */}
              <div className="sif-inputwrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className="sif-input no-edge-eye"
                  placeholder="Your password"
                  value={form.password}
                  onChange={handleChange}
                  maxLength={PASSWORD_MAX_LEN}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="sif-eye"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} />
                </button>
              </div>
              {errors.password && (
                <div className="sif-error">{errors.password}</div>
              )}
            </div>

            <div className="sif-row">
              <span />
              <a href="/forgot-password" className="sif-forgot">Forgot password?</a>
            </div>

            <button type="submit" className="sif-cta" disabled={loading}>
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          {/* Previous SSO row, moved below into the Explore Public Pages slot:
          {ssoProviders.length > 0 && (
            <>
              <div className="sif-or">or continue with</div>
              <div className="sif-sso-group">
                {ssoProviders.map((p) => {
                  const meta = getProviderMeta(p.provider_key);
                  return (
                    <button
                      key={p.provider_key}
                      type="button"
                      className="sif-sso"
                      onClick={() => handleSsoClick(p.provider_key)}
                    >
                      <i className={`bi ${meta.icon}`} />
                      Continue with {meta.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          */}

          {/* Explore Public Pages link — hidden; the SSO row below takes its place.
          <div className="sif-or">or</div>
          <button
            type="button"
            className="sif-sso sif-explore"
            onClick={() => navigate('/p/event')}
          >
            <i className="bi bi-globe2" />
            Explore Public Pages
            <i className="bi bi-arrow-right sif-explore-arrow" />
          </button>
          */}

          {/* SSO button row (D-08, D-09, D-10) — suppressed entirely when no active providers */}
          {ssoProviders.length > 0 && (
            <>
              <div className="sif-or">or continue with</div>
              <div className="sif-sso-group">
                {ssoProviders.map((p) => {
                  const meta = getProviderMeta(p.provider_key);
                  return (
                    <button
                      key={p.provider_key}
                      type="button"
                      className="sif-sso"
                      onClick={() => handleSsoClick(p.provider_key)}
                    >
                      {/* .sif-sso already spaces icon and label with `gap` */}
                      {p.provider_key === 'google' ? (
                        <GoogleIcon style={{ marginRight: 0 }} />
                      ) : (
                        <i className={`bi ${meta.icon}`} />
                      )}
                      Continue with {meta.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {showSignUp && (
            <p className="sif-meta">
              Don&apos;t have an account?{' '}
              <button
                type="button"
                className="sif-link"
                onClick={() => navigate(withNext('/sign-up'))}
              >
                Sign Up
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default SignInForm;
